import { useEffect } from "react";
import { useSeoPause } from "@/hooks/useSeoPause";

interface PageMeta {
  title: string;
  description: string;
  canonical?: string;
  noindex?: boolean;
  keywords?: string;
  /** Open Graph type. Defaults to "website". Use "article" for content/guide pages. */
  ogType?: "website" | "article" | "product" | "profile";
  /** One JSON-LD object or an array of them. Injected with data-page-jsonld and removed on unmount. */
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

const BASE_TITLE = "CreaFinancia";
const BASE_URL = "https://creafinancia.es";

function setMetaByName(name: string, content: string) {
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setMetaByProperty(property: string, content: string) {
  let el = document.querySelector(`meta[property="${property}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("property", property);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

export function usePageMeta({ title, description, canonical, noindex, keywords, ogType, jsonLd }: PageMeta) {
  const { enabled: seoPaused } = useSeoPause();
  const effectiveNoindex = noindex || seoPaused;
  useEffect(() => {
    // Title
    document.title = title.includes(BASE_TITLE) ? title : `${title} | ${BASE_TITLE}`;

    // Meta description
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute("content", description);

    // Canonical (always set; fall back to current path)
    const canonicalPath = canonical || window.location.pathname;
    const canonicalHref = canonicalPath.startsWith("http") ? canonicalPath : `${BASE_URL}${canonicalPath}`;
    let link = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.href = canonicalHref;

    // OG tags
    setMetaByProperty("og:title", document.title);
    setMetaByProperty("og:description", description);
    setMetaByProperty("og:url", canonicalHref);
    setMetaByProperty("og:type", ogType || "website");
    setMetaByName("twitter:title", document.title);
    setMetaByName("twitter:description", description);

    // Keywords (per-page override)
    if (keywords) setMetaByName("keywords", keywords);

    // Robots noindex
    let robotsMeta = document.querySelector('meta[name="robots"]');
    if (effectiveNoindex) {
      if (!robotsMeta) {
        robotsMeta = document.createElement("meta");
        robotsMeta.setAttribute("name", "robots");
        document.head.appendChild(robotsMeta);
      }
      robotsMeta.setAttribute("content", "noindex, nofollow");
    } else if (robotsMeta) {
      robotsMeta.setAttribute("content", "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1");
    }

    // Page-scoped JSON-LD
    const injected: HTMLScriptElement[] = [];
    if (jsonLd) {
      const items = Array.isArray(jsonLd) ? jsonLd : [jsonLd];
      items.forEach((data) => {
        const script = document.createElement("script");
        script.type = "application/ld+json";
        script.setAttribute("data-page-jsonld", "true");
        script.text = JSON.stringify(data);
        document.head.appendChild(script);
        injected.push(script);
      });
    }

    return () => {
      // Reset on unmount
      document.title = `${BASE_TITLE} – Crea tu empresa en España con IA`;
      const rm = document.querySelector('meta[name="robots"]');
      if (rm) rm.setAttribute("content", "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1");
      // Remove only the JSON-LD scripts we injected this mount
      injected.forEach((s) => s.parentNode?.removeChild(s));
    };
  }, [title, description, canonical, effectiveNoindex, keywords, ogType, jsonLd]);
}
